import { Box, Tooltip } from '@mui/material';
import PropTypes from 'prop-types';
import { useState } from 'react';
import { cloudinaryFull, cloudinaryThumbnail } from '../../utils/formatCloudinary';
import { fCurrency } from '../../utils/formatNumber';
import Image from '../image';
import Lightbox from '../lightbox';
import Iconify from '../iconify/Iconify';
import { fDateMonth } from '../../utils/formatTime';
import PreviousOrderCategory from './PreviousOrderCategory';
import PreviousOrderSubcategory from './PreviousOrderSubcategory';

export default function RenderImage({ params }) {
  RenderImage.propTypes = {
    params: PropTypes.object,
  };
  const { pictures } = params.row;
  const [selectedImage, setSelectedImage] = useState(-1);
  const handleOpenLightbox = () => {
    setSelectedImage(0);
  };
  const handleCloseLightbox = () => {
    setSelectedImage(-1);
    };
  return (
    <>
      <Image
        alt="attachment"
        src={pictures?.length ?
              cloudinaryThumbnail(pictures?.[0]) :
              cloudinaryThumbnail('https://res.cloudinary.com/agoraserv/image/upload/v1738258682/No%20Image%20Animation.gif')
            }
        sx={{
          cursor: 'pointer',
          maxWidth: '45px',
          maxHeight: '45px',
          '&:hover': {
            opacity: 0.9,
          },
        }}
        onClick={() => handleOpenLightbox()}
      />
      <Lightbox
        index={0}
        slides={pictures?.map((picture) => ({ src: cloudinaryFull(picture) }))}
        open={selectedImage >= 0}
        close={handleCloseLightbox}
      />
    </>
  );
}
const picture = {
  field: 'pictures',
  headerName: 'Image',
  width: 100,
  renderCell: (params) => <RenderImage params={params} />,
  sortable: false,
  filterable: false,
};
const description = {
  field: 'description',
  headerName: 'Description',
  width: 300,
  renderCell: (params) => (
    <Box
      component="div"
      sx={{
        fontSize: '0.7rem',
        display: '-webkit-box',
        WebkitBoxOrient: 'vertical',
        WebkitLineClamp: 3,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'pre-wrap',
      }}
    >
      {params.value}
    </Box>
  ),
  type: 'customSelect',
};

const price = {
  field: 'price',
  headerName: 'Price',
  width: 120,
  renderCell: (params) => fCurrency(params.row.price),
  valueGetter: (params) => params.row.price,
  type: 'customMoney',
  headerAlign: 'left',
  align: 'left',
};

const vendor = {
  field: 'vendor',
  headerName: 'Vendor',
  width: 100,
  type: 'customSelect',
};

const vendorItemId = {
  field: 'vendorItemId',
  headerName: 'Vendor Id',
  width: 150,
  renderCell: (params) => (
    <Box
      component="div"
      sx={{
        wordWrap: 'break-word',
        whiteSpace: 'pre-wrap',
      }}
    >
      {params.value}
    </Box>
  ),
  type: 'customSelect',
};

const uom = {
  field: 'uom',
  headerName: 'UOM',
  renderCell: (params) => params.value,
  width: 100,
  getFilterValues: (row) => [`${row.uomAmount ? row.uomAmount : ''}/${row.uomType ? row.uomType : ''}`],
  valueGetter: (params) => `${params.row.uomAmount ? params.row.uomAmount : ''}/${params.row.uomType ? params.row.uomType : ''}`,
  type: 'customSelect',
};

const perItem = {
  field: 'perItem',
  headerName: 'Per Item',
  width: 100,
  renderCell: (params) => params.row.price / params.row.uomAmount < .01 ? fCurrency(.01) : fCurrency(params.row.price / params.row.uomAmount),
  valueGetter: (params) => params.row.price / params.row.uomAmount,
  type: 'customMoney',
  headerAlign: 'left',
  align: 'left',
};
const total = {
  field: 'total',
  headerName: 'PO Total',
  width: 100,
  type: 'customMoney',
  renderCell: (params) => fCurrency(params.row.quantity * params.row.price),
  valueGetter: (params) => params.row.quantity * params.row.price,
};

const categoryDropdown = (readOnlyCategories) => ({
    field: 'categoryDropdown',
    headerName: 'Category',
    type: 'customSelect',
    onCellClick: (params) => {
        params.event.stopPropagation();
    },
    width: 120,
    renderCell: (params) => (
        <PreviousOrderCategory disabled={readOnlyCategories || params.row.deleted} row={params.row} />
    ),
    valueGetter: (params) => params.row.agoraCategory || '',
    path: 'agoraCategory',
});
const subCategoryDropdown = (readOnlyCategories) => ({
    field: 'subCategoryDropdown',
    headerName: 'Subcategory',
    type: 'customSelect',
    onCellClick: (params) => {
        params.event.stopPropagation();
    },
    width: 140,
    renderCell: (params) => (
        <PreviousOrderSubcategory disabled={readOnlyCategories || params.row.deleted} row={params.row} />
    ),
    valueGetter: (params) => params.row.agoraSubcategory || '',
    path: 'agoraSubcategory',
});

const agoraNumber = {
  field: 'productId',
  headerName: 'PS #',
  width: 100,
  renderCell: (params) => params.value,
  valueGetter: (params) => params.row.itemId !== -1 ? ` PS${params.row.productId}` : "Punchout Item",
  getFilterValues: (row) => (row.itemId !== -1 ? [` PS${row.productId}`] : ["Punchout Item"]),
  type: 'customSelect',
};

const getBudgetQtySum = (budgetQty) => {
  if(Array.isArray(budgetQty)) return budgetQty.reduce((a, b) => a + b, 0);
  return budgetQty;
};

const ordered = {
  field: 'ordered',
  headerName: 'Ordered/Approved',
  width: 50,
  renderCell: (params) => <Box sx={{display: 'flex', justifyContent: 'center', alignItems: 'center'}}>
    {params.row.originalQuantity} 
    <Box component="span" sx={{mx: '2px'}}>|</Box>
    <Tooltip
      arrow
      position="top"
      title={`PO Quantity: ${params.row.originalQuantity} | Budget breakdown last updated on ${fDateMonth(params.row.budgetUpdatedAt)}`}
      disableHoverListener={params.row.deleted === true}
    >
      <Box sx={{alignItems: 'center', display: 'flex'}}>
        {params.row.quantity !== getBudgetQtySum(params.row.budgetQty) && <Iconify icon="mdi:update" width={15}/>}
        {getBudgetQtySum(params.row.budgetQty)}
      </Box>
    </Tooltip>
  </Box>,
  type: 'customSelect',
  valueGetter: (params) => `${params.row.originalQuantity} | ${getBudgetQtySum(params.row.budgetQty)}`,
  getFilterValues: (row) => [`${row.originalQuantity} | ${getBudgetQtySum(row.budgetQty)}`],
};
const isPPD = {
  field: 'isPPD',
  headerName: 'PPD',
  renderCell: (params) => params.value,
  getFilterValues: (row) => (row.isPPD ? ['Yes'] : []),
  valueGetter: (params) => (params.value ? 'Yes' : ''),
  width: 100,
  type: 'customSelect',
};

const getOrderColumns = (readOnlyCategories = false) => [
  picture,
  description,
  vendorItemId,
  agoraNumber,
  uom,
  perItem,
  price,
  total,
  categoryDropdown(readOnlyCategories),
  subCategoryDropdown(readOnlyCategories),
  vendor,
  ordered,
  isPPD,
];
const COPY_ORDER_VIEW = [picture, description, ordered, price, total, vendor];

export { getOrderColumns, COPY_ORDER_VIEW };
